import { Constants } from '../shared/Constants';

/**
 * ToolbarBuilder Class
 */
export class ToolbarBuilder {
    constructor() {
        this._buttons = [];
        this._customButtonCounter = 0;
    }

    csv() {
        const btn = { key: Constants.Buttons.CSV, title: 'CSV', icon: Constants.Icons.DOWNLOAD, color: '#3b82f6', callback: null, showIf: null, textColor: null };
        this._buttons.push(btn);
        return this._wrap(btn);
    }

    excel() {
        const btn = { key: Constants.Buttons.EXCEL, title: 'Excel', icon: Constants.Icons.DOWNLOAD, color: '#10b981', callback: null, showIf: null, textColor: null };
        this._buttons.push(btn);
        return this._wrap(btn);
    }

    print() {
        const btn = { key: Constants.Buttons.PRINT, title: 'Print', icon: Constants.Icons.PRINT, color: '#64748b', callback: null, showIf: null, textColor: null };
        this._buttons.push(btn);
        return this._wrap(btn);
    }

    copy() {
        const btn = { key: Constants.Buttons.COPY, title: 'Copy', icon: 'bi-clipboard', color: '#6366f1', callback: null, showIf: null, textColor: null };
        this._buttons.push(btn);
        return this._wrap(btn);
    }

    pdf() {
        const btn = { key: Constants.Buttons.PDF, title: 'PDF', icon: Constants.Icons.DOWNLOAD, color: '#ef4444', callback: null, showIf: null, textColor: null };
        this._buttons.push(btn);
        return this._wrap(btn);
    }

    custom(key) {
        const btn = {
            key: this._normalizeKey(key),
            title: null,
            icon: null,
            color: Constants.Theme.SECONDARY,
            callback: null,
            showIf: null,
            textColor: null
        };
        this._buttons.push(btn);
        return this._wrap(btn);
    }

    colvis() {
        const btn = { key: Constants.Buttons.COLVIS, title: 'Columns', icon: Constants.Icons.LAYOUT, color: '#ff9447', callback: null, showIf: null, textColor: null };
        this._buttons.push(btn);
        return this._wrap(btn);
    }

    /**
     * Creates a segmented toolbar button group (like "Buttons 1 | Buttons 2 | Buttons 3").
     * Each segment is a clickable button with its own callback.
     *
     * @param {string} [key]
     * @param {function({ button: function(string): any, item: function(string): any })|Array<Object>} itemsFnOrArray
     */
    group(key, itemsFnOrArray) {
        const groupKey = this._normalizeKey(key || 'group');
        const btn = {
            key: groupKey,
            isGroup: true,
            className: '',
            groupItems: [],
            callback: null,
            color: null,
            textColor: null
        };
        this._buttons.push(btn);
        return this._wrap(btn).asGroup(itemsFnOrArray);
    }

    _wrap(btn) {
        const wrapItem = (item) => ({
            withTitle: (t) => { item.title = t; return wrapItem(item); },
            withIcon: (i) => { item.icon = i; return wrapItem(item); },
            withBackgroundColor: (c) => { item.color = c; return wrapItem(item); },
            withTextColor: (c) => { item.textColor = c; return wrapItem(item); },
            withClass: (c) => { item.className = c; return wrapItem(item); },
            showIf: (conditionFn) => { item.showIf = conditionFn; return wrapItem(item); },
            onClick: (cb) => { item.callback = cb; return wrapItem(item); },
            onProcess: (cb) => { item.callback = cb; return wrapItem(item); }
        });

        const groupItemBuilder = () => {
            const add = (key) => {
                const normalizedKey = this._normalizeKey(key);
                const item = { key: normalizedKey, id: null, title: null, icon: null, className: '', callback: null, color: null, textColor: null, showIf: null };
                btn.groupItems = btn.groupItems || [];
                btn.groupItems.push(item);
                return wrapItem(item);
            };
            
            return {
                button: add,
                item: add
            };
        };

        const itemBuilder = () => ({
            item: (key) => {
                const normalizedKey = this._normalizeKey(key);
                const item = { key: normalizedKey, title: key ?? normalizedKey, icon: null, className: '', callback: null, textColor: null, showIf: null };
                btn.splitItems = btn.splitItems || [];
                btn.splitItems.push(item);
                return wrapItem(item);
            }
        });

        return {
            withTitle: (t) => { btn.title = t; return this._wrap(btn); },
            withIcon: (i) => { btn.icon = i; return this._wrap(btn); },
            withClass: (c) => { btn.className = c; return this._wrap(btn); },
            withBackgroundColor: (c) => { btn.color = c; return this._wrap(btn); },
            withTextColor: (c) => { btn.textColor = c; return this._wrap(btn); },
            showIf: (conditionFn) => { btn.showIf = conditionFn; return this._wrap(btn); },
            onClick: (cb) => { btn.callback = cb; return this._wrap(btn); },
            onProcess: (cb) => { btn.callback = cb; return this._wrap(btn); },
            withSplitDivider: (enabled = true) => { btn.splitDivider = Boolean(enabled); return this._wrap(btn); },
            asSplit: (itemsFnOrArray) => {
                btn.isSplit = true;
                btn.splitItems = btn.splitItems || [];

                if (typeof itemsFnOrArray === 'function') {
                    itemsFnOrArray(itemBuilder());
                } else if (Array.isArray(itemsFnOrArray)) {
                    itemsFnOrArray.forEach(raw => {
                        if (!raw) return;
                        const key = this._normalizeKey(raw.key || raw.title || 'item');
                        btn.splitItems.push({
                            key,
                            title: raw.title ?? raw.key ?? key,
                            icon: raw.icon ?? null,
                            className: raw.className ?? '',
                            callback: raw.callback ?? null,
                            textColor: null,
                            showIf: null
                        });
                    });
                }

                return this._wrap(btn);
            },
            asGroup: (itemsFnOrArray) => {
                btn.isGroup = true;
                btn.groupItems = btn.groupItems || [];

                if (typeof itemsFnOrArray === 'function') {
                    itemsFnOrArray(groupItemBuilder());
                } else if (Array.isArray(itemsFnOrArray)) {
                    itemsFnOrArray.forEach(raw => {
                        if (!raw) return;
                        const key = this._normalizeKey(raw.key || raw.title || 'item');
                        btn.groupItems.push({
                            key,
                            title: raw.title ?? raw.key ?? key,
                            icon: raw.icon ?? null,
                            className: raw.className ?? '',
                            color: raw.color ?? null,
                            textColor: raw.textColor ?? null,
                            callback: raw.callback ?? null,
                            showIf: null
                        });
                    });
                }

                return this._wrap(btn);
            }
        };
    }

    _normalizeKey(key) {
        if (typeof key === 'string') {
            const normalized = key.trim().toLowerCase().replace(/\s+/g, '-');
            if (normalized) return normalized;
        }

        this._customButtonCounter += 1;
        return `custom-toolbar-${this._customButtonCounter}`;
    }

    _getButtons() {
        return this._buttons;
    }
}
